import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Card from 'components/Layout/Card';
import {
  getCCAvenuePaymentURL,
  showSuccess,
  storeCCAvenuePayment,
  throwError,
} from 'store/slices';
import PayPalPayment from './PayPalPayment';
import Button from 'components/form/Button';
import Loader from 'components/Layout/Loader';
import { decrypt, encrypt, objectToFormData } from 'utils/helpers';
import HeaderLayout from 'components/Layout/HeaderLayout';

const PaymentPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const htmlElRef = useRef(null);

  const [isLoader, setIsLoader] = useState(false);
  const [payURL, setPayURL] = useState('');
  const [btnIsLoading, setBtnIsLoading] = useState(false);
  const [id, setId] = useState(null); // Replace with actual ID if needed

  const [paymentParams, setPaymentParams] = useState({
    amount: '',
    currency: '',
    reference_id: '',
    abstract_id: '',
  });
  const [successStatus, setSuccessStatus] = useState(null);

  const { isSessionExpired } = useSelector((state) => ({
    isSessionExpired: state.global.isSessionExpired,
  }));

  // Extract & decrypt query parameters from URL
  useEffect(() => {
    const queryString = window.location.search;
    const encodedData = queryString.substring(1);

    try {
      const decoded = decodeURIComponent(encodedData);
      const decrypted = decrypt(decoded);
      const params = new URLSearchParams(decrypted);

      setPaymentParams({
        amount: params.get('amount') || '',
        currency: params.get('currency') || '',
        reference_id: params.get('reference_id') || '',
        abstract_id: params.get('abstract_id') || '',
      });

      setId(params.get('abstract_id')); // or use another ID field if required
    } catch (error) {
      console.error('Error decoding/decrypting query params:', error);
    }
  }, []);

  // Auto trigger payment iframe if valid and not redirected
  useEffect(() => {
    const resParams = window.location.search?.split('?response=')?.pop();

    if (isSessionExpired) return;

    if (resParams) {
      localStorage.paymentResponse = resParams;
      navigate(window.location.pathname); // clear params
    } else {
      fetchCCAvenueURL(); // auto trigger
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSessionExpired]);

  // Handle CCAvenue response and trigger save
  useEffect(() => {
    if (localStorage.paymentIntent) {
      let localInitValue = {};
      let localResponse = {};
      if (localStorage.paymentIntent) {
        localInitValue = JSON.parse(decrypt(localStorage.paymentIntent));
      }
      if (localStorage.paymentResponse) {
        localResponse = decrypt(localStorage.paymentResponse);
      }

      const { order_status, status_message } = localResponse;
      if (order_status === 'Success') {
        setBtnIsLoading(true);
        dispatch(showSuccess(status_message));
        setTimeout(() => {
          handelSave({
            id: id,
            payment_method: localInitValue.currency,
            amount: localInitValue?.amount || 0,
            order_id: localResponse?.order_id || '',
            payment_id: localResponse?.tracking_id || '',
          });
        }, 1500);
      } else if (status_message) {
        dispatch(
          throwError({
            message: status_message,
          })
        );
      }

      setTimeout(() => {
        htmlElRef?.current?.scrollIntoView({ behavior: 'smooth' });
        localStorage.removeItem('paymentResponse');
        localStorage.removeItem('paymentIntent');
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch CCAvenue Payment URL and store intent
  const fetchCCAvenueURL = async () => {
    setIsLoader(true);

    const orderId = Math.random().toString(36).slice(2);

    const payload = {
      type: 'EVENT_PASS_REGISTER',
      amount: paymentParams.amount,
      currency: paymentParams.currency,
      merchant_id: process.env.REACT_APP_MERCHANT_ID,
      order_id: orderId,
      redirect_url: 'https://stage-dashboard.iferp.in/ccavResponseHandler.php',
      cancel_url: 'https://stage-dashboard.iferp.in/ccavResponseHandler.php',
      language: 'EN',
      integration_type: 'iframe_normal',
      billing_zip: '',
      billing_name: '',
      billing_address: '',
      billing_city: '',
      billing_state: '',
      billing_country: '',
      billing_tel: '',
      billing_email: '',
    };

    try {
      localStorage.setItem('paymentIntent', encrypt(JSON.stringify(payload)));

      const response = await dispatch(getCCAvenuePaymentURL(payload));
      setPayURL(response?.data || '');
    } catch (error) {
      console.error('Failed to fetch CCAvenue URL:', error);
    } finally {
      setIsLoader(false);
    }
  };

  const handelSave = async (data) => {
    const payload = {
      order_id: data?.order_id || '',
      payment_id: data?.tracking_id || '',
      abstract_id: paymentParams?.abstract_id || '',
    };
    const response = await dispatch(
      storeCCAvenuePayment(objectToFormData(payload))
    );
    if (response?.status === 200) {
      navigate('/');
      window.location.reload();
    }
    setBtnIsLoading(false);
  };

  return (
    <HeaderLayout>
      <div id="forgot-password-container" ref={htmlElRef}>
        <Card className="card-padding ps-5 pe-5 pt-5 pb-5">
          <div>
            {params.payment_type === 'paypal' ? (
              <>
                {successStatus === true && (
                  <div>
                    <p className="text-26-400 text-success cpb-10">
                      Payment Successful!
                    </p>
                    <Button
                      text="Back to Home"
                      onClick={() => navigate('/')}
                      btnStyle="primary-outline1"
                      isRounded
                    />
                  </div>
                )}
                {successStatus === false && (
                  <div>
                    <p className="text-26-400 text-danger">
                      Payment Failed. Please try again.
                    </p>
                    <Button
                      text="Back to Home"
                      onClick={() => navigate('/')}
                      btnStyle="primary-outline1"
                      isRounded
                    />
                  </div>
                )}
                {successStatus === null && (
                  <PayPalPayment
                    data={paymentParams}
                    setSuccessStatus={setSuccessStatus}
                  />
                )}
              </>
            ) : (
              <div className="text-center">
                <Button
                  text="Pay with CCAvenue"
                  onClick={fetchCCAvenueURL}
                  isRounded
                  btnStyle="primary-dark"
                  className="cps-40 cpe-40 mb-4"
                />

                {isLoader ? (
                  <Loader size="lg" />
                ) : payURL ? (
                  <iframe
                    width="100%"
                    height="600px"
                    frameBorder="0"
                    id="paymentFrame"
                    src={payURL}
                    title="CCAvenue Payment"
                    style={{ overflow: 'auto' }}
                  />
                ) : (
                  <p className="text-muted">Payment page will appear here.</p>
                )}
              </div>
            )}
          </div>
        </Card>
      </div>
    </HeaderLayout>
  );
};

export default PaymentPage;
